import React, { useState, useEffect } from 'react';
import { User, Mail, Lock, Save, Eye, EyeOff, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import api from '../../services/api';

const AccountSettings = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // Account info
  const [accountData, setAccountData] = useState({
    username: '',
    email: '',
  });

  // Password change
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Email configuration
  const [emailConfig, setEmailConfig] = useState({
    smtp_host: '',
    smtp_port: '587',
    smtp_user: '',
    smtp_password: '',
    admin_email: '',
  });

  useEffect(() => {
    loadAccountData();
    loadEmailConfig();
  }, []);

  const loadAccountData = async () => {
    setLoading(true);
    try {
      const response = await api.get('/auth/me');
      setAccountData({
        username: response.data.username,
        email: response.data.email,
      });
    } catch (error) {
      console.error('Error loading account:', error);
      toast.error('Erreur lors du chargement du compte');
    } finally {
      setLoading(false);
    }
  };

  const loadEmailConfig = async () => {
    try {
      const response = await api.get('/email-config');
      if (response.data) {
        setEmailConfig(response.data);
      }
    } catch (error) {
      console.error('Error loading email config:', error);
    }
  };

  const handleUpdateAccount = async () => {
    setSaving(true);
    try {
      await api.put('/auth/update-account', accountData);
      toast.success('Compte mis à jour avec succès');
    } catch (error) {
      console.error('Error updating account:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setSaving(true);
    try {
      await api.put('/auth/change-password', {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
      });
      toast.success('Mot de passe changé avec succès');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEmailConfig = async () => {
    setSaving(true);
    try {
      await api.put('/email-config', emailConfig);
      toast.success('Configuration email sauvegardée');
    } catch (error) {
      console.error('Error saving email config:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <h2 className="text-2xl font-bold text-white">Paramètres du Compte</h2>

      {/* Account Information */}
      <div className="glass rounded-xl p-6 space-y-4">
        <h3 className="text-xl font-semibold text-white flex items-center gap-2">
          <User className="w-5 h-5 text-blue-400" />
          Informations du Compte
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Nom d'utilisateur
            </label>
            <Input
              value={accountData.username}
              onChange={(e) => setAccountData({ ...accountData, username: e.target.value })}
              className="bg-slate-700 border-slate-600 text-white"
              placeholder="Nom d'utilisateur"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Email
            </label>
            <Input
              type="email"
              value={accountData.email}
              onChange={(e) => setAccountData({ ...accountData, email: e.target.value })}
              className="bg-slate-700 border-slate-600 text-white"
              placeholder="email@example.com"
            />
          </div>

          <Button
            onClick={handleUpdateAccount}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sauvegarde...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Sauvegarder
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Change Password */}
      <div className="glass rounded-xl p-6 space-y-4">
        <h3 className="text-xl font-semibold text-white flex items-center gap-2">
          <Lock className="w-5 h-5 text-purple-400" />
          Changer le Mot de Passe
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Mot de passe actuel
            </label>
            <div className="relative">
              <Input
                type={showPasswords.current ? 'text' : 'password'}
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white pr-12"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Nouveau mot de passe
            </label>
            <div className="relative">
              <Input
                type={showPasswords.new ? 'text' : 'password'}
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white pr-12"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Confirmer le nouveau mot de passe
            </label>
            <div className="relative">
              <Input
                type={showPasswords.confirm ? 'text' : 'password'}
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white pr-12"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <Button
            onClick={handleChangePassword}
            disabled={saving || !passwordData.currentPassword || !passwordData.newPassword}
            className="bg-purple-600 hover:bg-purple-700"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Changement...
              </>
            ) : (
              <>
                <Lock className="w-4 h-4 mr-2" />
                Changer le Mot de Passe
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Email Configuration */}
      <div className="glass rounded-xl p-6 space-y-4">
        <h3 className="text-xl font-semibold text-white flex items-center gap-2">
          <Mail className="w-5 h-5 text-green-400" />
          Configuration Email (SMTP)
        </h3>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Serveur SMTP
              </label>
              <Input
                value={emailConfig.smtp_host}
                onChange={(e) => setEmailConfig({ ...emailConfig, smtp_host: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="smtp.gmail.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Port SMTP
              </label>
              <Input
                type="number"
                value={emailConfig.smtp_port}
                onChange={(e) => setEmailConfig({ ...emailConfig, smtp_port: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="587"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Email d'envoi (SMTP User)
            </label>
            <Input
              type="email"
              value={emailConfig.smtp_user}
              onChange={(e) => setEmailConfig({ ...emailConfig, smtp_user: e.target.value })}
              className="bg-slate-700 border-slate-600 text-white"
              placeholder="votre-email@gmail.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Mot de passe SMTP
            </label>
            <Input
              type="password"
              value={emailConfig.smtp_password}
              onChange={(e) => setEmailConfig({ ...emailConfig, smtp_password: e.target.value })}
              className="bg-slate-700 border-slate-600 text-white"
              placeholder="••••••••••••••••"
            />
            <p className="text-xs text-gray-400 mt-1">
              Pour Gmail, utilisez un mot de passe d'application
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Email Admin (destinataire)
            </label>
            <Input
              type="email"
              value={emailConfig.admin_email}
              onChange={(e) => setEmailConfig({ ...emailConfig, admin_email: e.target.value })}
              className="bg-slate-700 border-slate-600 text-white"
              placeholder="admin@evocore-paca.fr"
            />
            <p className="text-xs text-gray-400 mt-1">
              Email qui recevra les notifications de contact
            </p>
          </div>

          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <p className="text-sm text-blue-300">
              <strong>Info :</strong> Ces paramètres permettent d'envoyer des emails automatiques lors de la réception de messages de contact.
            </p>
          </div>

          <Button
            onClick={handleSaveEmailConfig}
            disabled={saving}
            className="bg-green-600 hover:bg-green-700"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sauvegarde...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Sauvegarder la Configuration Email
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;
